import { create } from 'zustand';
import { supabase } from '@/utils/supabase';
import type { Voucher } from '@/utils/supabase';
import { getCacheBuster } from './refreshStore';

interface CouponState {
  coupons: Voucher[];
  isLoading: boolean;
  error: string | null;
  fetchCoupons: () => Promise<void>;
  addCoupon: (coupon: Omit<Voucher, 'id' | 'created_at' | 'usage_count'>, signal?: AbortSignal) => Promise<Voucher | null>;
  updateCoupon: (id: string, updates: Partial<Voucher>, signal?: AbortSignal) => Promise<Voucher | null>;
  deleteCoupon: (id: string, signal?: AbortSignal) => Promise<boolean>;
  validateCoupon: (code: string, orderAmount: number, signal?: AbortSignal) => Promise<{ coupon: Voucher | null, error: string | null, minAmount?: number }>;
}

export const useCouponStore = create<CouponState>((set, get) => ({
  coupons: [],
  isLoading: false,
  error: null,

  fetchCoupons: async () => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('vouchers')
        .select('*')
        .neq('id', getCacheBuster())
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ coupons: data || [], isLoading: false });
    } catch (error: unknown) {
      if (import.meta.env.DEV) console.error(error); set({ error: 'An unexpected error occurred.', isLoading: false });
    }
  },

  addCoupon: async (couponData, signal) => {
    set({ isLoading: true, error: null });
    try {
      let query = supabase
        .from('vouchers')
        .insert([couponData])
        .select();
        
      if (signal) {
        query = query.abortSignal(signal);
      }
        
      const { data, error } = await query.single();

      if (error) throw error;
      const { coupons } = get();
      set({ coupons: [data, ...coupons], isLoading: false });
      return data;
    } catch (error: unknown) {
      if (import.meta.env.DEV) console.error(error); set({ error: 'An unexpected error occurred.', isLoading: false });
      return null;
    }
  },

  updateCoupon: async (id, updates, signal) => {
    set({ isLoading: true, error: null });
    try {
      let query = supabase
        .from('vouchers')
        .update(updates)
        .eq('id', id)
        .select();
        
      if (signal) {
        query = query.abortSignal(signal);
      }
        
      const { data, error } = await query.single();

      if (error) throw error;
      const { coupons } = get();
      set({
        coupons: coupons.map(c => c.id === id ? data : c),
        isLoading: false
      });
      return data;
    } catch (error: unknown) {
      if (import.meta.env.DEV) console.error(error); set({ error: 'An unexpected error occurred.', isLoading: false });
      return null;
    }
  },

  deleteCoupon: async (id, signal) => {
    set({ isLoading: true, error: null });
    try {
      let query = supabase
        .from('vouchers')
        .delete()
        .eq('id', id);
        
      if (signal) {
        query = query.abortSignal(signal);
      }
        
      const { error } = await query;

      if (error) throw error;
      const { coupons } = get();
      set({
        coupons: coupons.filter(c => c.id !== id),
        isLoading: false
      });
      return true;
    } catch (error: unknown) {
      if (import.meta.env.DEV) console.error(error); set({ error: 'An unexpected error occurred.', isLoading: false });
      return false;
    }
  },

  validateCoupon: async (code, orderAmount, signal) => {
    const trimmedCode = code.trim();
    set({ isLoading: true, error: null });
    try {
      let rpcCall = supabase.rpc('validate_coupon', { 
        p_code: trimmedCode, 
        p_order_amount: orderAmount 
      });

      if (signal) {
        rpcCall = rpcCall.abortSignal(signal);
      }

      const { data, error } = await rpcCall;

      if (error) throw error;

      if (!data.valid) {
        if (data.error.includes('Minimum order amount')) {
            return { coupon: null, error: 'MIN_AMOUNT', minAmount: data.min_order_amount };
        }
        return { coupon: null, error: data.error };
      }

      return { coupon: data.voucher as Voucher, error: null };
    } catch (error: unknown) {
      if (import.meta.env.DEV) console.error(error); set({ error: 'An unexpected error occurred.' });
      return { coupon: null, error: error instanceof Error ? error.message : String(error) };
    } finally {
      set({ isLoading: false });
    }
  }
}));
